import { useQuery } from '@tanstack/react-query';
import { fetchEditableProfile, fetchMyDayOffStatus, fetchMyHealthPlan, fetchRequestContext } from '../../services/dpRequestCallables';
import { getDpRequest, listDpMessages, listDpQueue, listManagerDayOffs, listMyDpRequests } from '../../services/dpRequestQueries';
import { dpRequestsKeys } from './dpRequestsQueryKeys';

const NON_RETRYABLE_CODES = ['permission-denied', 'failed-precondition', 'unauthenticated'];

function retryUnlessDenied(failureCount: number, error: unknown): boolean {
    const code = (error as { code?: string } | null)?.code ?? '';
    if (NON_RETRYABLE_CODES.some(item => code.includes(item))) return false;
    return failureCount < 2;
}

export function useMyDpRequestsQuery(email: string | null | undefined) {
    return useQuery({
        queryKey: dpRequestsKeys.mine(email ?? ''),
        queryFn: () => listMyDpRequests(email as string),
        enabled: Boolean(email),
        retry: retryUnlessDenied,
    });
}

export function useDpQueueQuery(enabled: boolean) {
    return useQuery({
        queryKey: dpRequestsKeys.queue(),
        queryFn: listDpQueue,
        enabled,
        retry: retryUnlessDenied,
    });
}

export function useManagerDayOffsQuery(email: string | null | undefined) {
    return useQuery({
        queryKey: dpRequestsKeys.manager(email ?? ''),
        queryFn: () => listManagerDayOffs(email as string),
        enabled: Boolean(email),
        retry: retryUnlessDenied,
    });
}

export function useDpRequestQuery(requestId: string | null | undefined) {
    return useQuery({
        queryKey: dpRequestsKeys.detail(requestId ?? ''),
        queryFn: () => getDpRequest(requestId as string),
        enabled: Boolean(requestId),
        retry: retryUnlessDenied,
    });
}

export function useDpMessagesQuery(requestId: string | null | undefined) {
    return useQuery({
        queryKey: dpRequestsKeys.messages(requestId ?? ''),
        queryFn: () => listDpMessages(requestId as string),
        enabled: Boolean(requestId),
        retry: retryUnlessDenied,
    });
}

export function useEditableProfileQuery(enabled: boolean) {
    return useQuery({
        queryKey: dpRequestsKeys.editableProfile(),
        queryFn: fetchEditableProfile,
        enabled,
        staleTime: 0,
    });
}

export function useRequestContextQuery(enabled: boolean) {
    return useQuery({
        queryKey: dpRequestsKeys.requestContext(),
        queryFn: fetchRequestContext,
        enabled,
        staleTime: 60_000,
    });
}

export function useMyHealthPlanQuery(enabled: boolean) {
    return useQuery({
        queryKey: dpRequestsKeys.healthPlan(),
        queryFn: fetchMyHealthPlan,
        enabled,
        staleTime: 0,
    });
}

export function useMyDayOffStatusQuery(enabled: boolean) {
    return useQuery({
        queryKey: dpRequestsKeys.dayOffStatus(),
        queryFn: fetchMyDayOffStatus,
        enabled,
        staleTime: 60_000,
        retry: retryUnlessDenied,
    });
}
